import { useState, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { VERIFY_STORE } from '../graphql/queries';

export interface AuthStore {
  clientId: string;
  storeId: string;
  storeName: string;
  portal?: 'manufacturer' | 'distributor' | string;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  store: AuthStore | null;
}

export const useAuth = (token: string | null): AuthState => {
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    error: null,
    store: null,
  });

  const [verifyStore, { data, error, loading }] = useLazyQuery(VERIFY_STORE, {
    fetchPolicy: 'network-only',
  });

  // Fire the query once when token is available
  useEffect(() => {
    if (!token) {
      setAuthState({ isAuthenticated: false, isLoading: false, error: 'No token provided', store: null });
      return;
    }
    verifyStore({ variables: { token } });
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to query result changes
  useEffect(() => {
    if (loading) return;

    if (error) {
      setAuthState({ isAuthenticated: false, isLoading: false, error: error.message, store: null });
      return;
    }

    if (data) {
      const result = data.verifyStore;
      if (result.error) {
        setAuthState({ isAuthenticated: false, isLoading: false, error: result.message, store: null });
      } else {
        setAuthState({
          isAuthenticated: true,
          isLoading: false,
          error: null,
          store: {
            clientId: result.clientId,
            storeId: result.storeId,
            storeName: result.storeName,
            portal: result.portal,
          },
        });
      }
    }
  }, [data, error, loading]);

  return authState;
};
